import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams } from 'src/supportPortal/services/services'

// Reverse-chronological "previous communication history" for a contact (WhatsApp / email / voice),
// backing the support call surface's Activity panel. Wraps GET /cc/support/communication-logs
// (CommunicationLogSearch + Pageable) — see docs/SUPPORT_CALL_PAGE_PLAN.md.
export async function getSupportCommunicationLogs({
  patientId,
  practitionerId,
  bookedEventId,
  page = 0,
  size = 20,
  sort = 'sentAt,desc',
} = {}) {
  if (!patientId && !practitionerId) {
    return { ok: false, message: 'A patient or practitioner is required.', logs: [], totalElements: 0, page: 0 }
  }

  const params = {
    ...(patientId && { patientId }),
    ...(practitionerId && { practitionerId }),
    ...(bookedEventId && { bookedEventId }),
    page,
    size,
    sort,
  }

  try {
    const response = await getSPDataWithParams('/api/secure/cc/support/communication-logs', params)
    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const data = getDataMapFromCommonResponse(response)?.['communication-logs']
      return {
        ok: true,
        logs: data?.content || [],
        totalElements: data?.totalElements || 0,
        page: data?.pageable?.pageNumber ?? page,
        size: data?.pageable?.pageSize ?? size,
      }
    }
    return {
      ok: false,
      message:
        response?.data?.message ||
        response?.response?.data?.message ||
        'Unable to load communication history.',
      logs: [],
      totalElements: 0,
      page: 0,
    }
  } catch (error) {
    console.error('Error fetching support communication logs:', error)
    return { ok: false, message: 'Unable to load communication history.', logs: [], totalElements: 0, page: 0 }
  }
}

// Support-agent interaction history (whatsapp / email / voice, incl. call-outcome dispositions) for
// a contact, from GET /contact-actions/history (SupportAgentCommunicationLog). This is a DIFFERENT
// collection from getSupportCommunicationLogs above (CommunicationLogEntry / template sends) — the
// call surface's Activity panel uses this one so calls show up. See docs/SUPPORT_CALL_PAGE_PLAN.md §0.
export async function getSupportContactHistory({
  patientId,
  practitionerId,
  bookedEventId,
  clinicBrandId,
  clinicId,
  targetRole,
  eventType,
  limit = 50,
} = {}) {
  if (!patientId && !practitionerId) {
    return { ok: false, message: 'A patient or practitioner is required.', logs: [] }
  }

  const params = {
    ...(patientId && { patientId }),
    ...(practitionerId && { practitionerId }),
    ...(bookedEventId && { bookedEventId }),
    ...(clinicBrandId && { clinicBrandId }),
    ...(clinicId && { clinicId }),
    ...(targetRole && { targetRole }),
    ...(eventType && { eventType }),
    limit,
  }

  try {
    const response = await getSPDataWithParams('/api/secure/cc/support/contact-actions/history', params)
    if (response?.status === 200 && isCommonResponseSuccessfulV2(response)) {
      const data = getDataMapFromCommonResponse(response)
      return {
        ok: true,
        logs: data?.logs || [],
        targetDisplayName: data?.targetDisplayName || '',
      }
    }
    return {
      ok: false,
      message:
        response?.data?.message ||
        response?.response?.data?.message ||
        'Unable to load communication history.',
      logs: [],
    }
  } catch (error) {
    console.error('Error fetching support contact history:', error)
    return { ok: false, message: 'Unable to load communication history.', logs: [] }
  }
}
